import { DeleteOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { Col, DatePicker, Empty, Form, Modal, Row, Tabs } from 'antd';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import CustomButton from '../../../../../v2-components/Antd/Button';
import CustomCard from '../../../../../v2-components/Antd/Card';
import CustomInput from '../../../../../v2-components/Antd/Input';
import CustomModal from '../../../../../v2-components/Antd/Modal';
import CustomUpload from '../../../../../v2-components/Antd/Upload';
import useSearchStore from '../../../../../v2-components/Search/store/useSearchStore';
import ProductSection from '../ComercialProducts/index';
import MagistralFinishedSection from '../MagistralFinished';
import MagistralToMakeSection from '../MagistralToMake';
import dayjs from 'dayjs';
import CustomMediaViewer from '../../../../../v2-components/CustomMediaViewer';
import { isRealId } from '../../handle/isRealId.handle';

export function uid() {
  return Math.random().toString(36).slice(2, 9);
}

const disabledDate = (current) => {
  return current && current < dayjs().startOf('day');
};


const calculateMagistralData = (product) => {

  const quantity = Number(product.quantity || 0);
  const priceUnit = Number(product.price_unit || product.price || 0);

  let discountPercent = 0;
  if (product.discount && typeof product.discount === 'object') {
      discountPercent = Number(product.discount.value || 0);
  } else {
      discountPercent = Number(product.discount_percent || 0);
  }
  const discountAmount = priceUnit * (discountPercent / 100);
  const priceAfterDiscount = priceUnit - discountAmount;
  const finalPrice = priceAfterDiscount * quantity;

  return {
    ...product,
    quantity: quantity,
    discount_percent: discountPercent,
    final_price: finalPrice,
    price_unit: priceUnit,
    final_price_unit: priceAfterDiscount
  };
};


const mapMagistralToMakeData = (product) => {

  const priceUnit = Number(product.price_unit || product.price);


  let discountPercent = 0;
  if (product.discount && typeof product.discount === 'object') {
    discountPercent = Number(product.discount.value);
  } else {
    discountPercent = Number(product.discount_percent);
  }

  const quantity = Number(product.quantity);

  const discountAmount = priceUnit * (discountPercent / 100);
  const priceFinalUnit = priceUnit - discountAmount;
  const finalPrice = priceFinalUnit * quantity;

  return {
    ...product,
    price_unit: priceUnit,
    discount_percent: discountPercent,
    quantity: quantity,
    
    price_final_unit: priceFinalUnit,
    final_price: finalPrice,
    
    form_unit: product.form_quantity_unit || product.form_unit, 
    form_quantity: product.form_quantity,

    components: (product.components || []).map(c => ({
        ...c,
        component_id: c.component_name, 
    }))
  };
};

const mapRecipesFromProps = (data) => {
  return (data || []).map((r) => ({
    id: r.id || uid(),
    imageUrl: r.url || r.imageUrl || null,
    patient: r.patient || { first_name: '', last_name: '', rut: '' },
    professional: r.professional || { first_name: '', last_name: '', rut: '', medical_center: '', email: '' },
    expiration_date: r.expiration_date || null,
    renewal_date: r.renewal_date || null,
    check_number: r.check_number || null,
    products: { commercial: r.products?.commercial || [], magistralFinished: (r.products?.magistralFinished || []).map(calculateMagistralData), magistralToMake: (r.products?.magistralToMake || []).map(mapMagistralToMakeData) },
  }));
};

export function RecipesModule({
  handleRemoveMagistralProduct,
  handleRemoveCommercialProductService,
  handleRemoveRecipeService,
  handleAddRecipeService,
  handleChangeRecipeService,
  statusNumber,
  initialRecipes = [],
  onChange = () => { },
  onPersist = () => { },
}) {

 

  const toggleSectionVisibility = (recipeId, section, isVisible) => {
    setRecipes(prev => prev.map(r => {
        if (r.id === recipeId) {
            return {
                ...r,
                uiState: {
                    ...r.uiState,
                    [section]: isVisible
                }
            };
        }
        return r;
    }));
};

const [isCreating, setIsCreating] = useState(false);
const [isUpdatingImage, setIsUpdatingImage] = useState(false); 

  const [recipes, setRecipes] = useState(() => {
    const localData = mapRecipesFromProps(initialRecipes);

    if (localData.length > 0) {
      const initialZustandState = {};
      for (const recipe of localData) {
        initialZustandState[recipe.id] = recipe.products.commercial || [];
      }
      useSearchStore.getState().initializeRecipeProducts(initialZustandState);
    }

    return localData;
  });

  const [activeKey, setActiveKey] = useState(recipes.length ? recipes[0].id : null);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [fileList, setFileList] = useState([]);
  console.log('recipes', recipes)

  useEffect(() => {
    onChange(recipes);
  }, [recipes, onChange]);



  useEffect(() => {
    if (initialRecipes) { // Quitamos el check de length > 0 para permitir vaciar si es necesario, aunque el map lo maneja.
      const incomingRecipes = mapRecipesFromProps(initialRecipes);
      
      // Sincronizar Zustand con lo que viene de la DB (solo si es necesario, para no borrar estado local)
      // Nota: Esto podría ser peligroso si sobrescribe trabajo en curso. 
      // Idealmente zustand debe ser la fuente de verdad para productos comerciales.
      // Si incomingRecipes trae productos guardados, aseguramos que estén en zustand.
      const newZustandState = useSearchStore.getState().productsByRecipe;
      let zustandUpdated = false;
      
      for (const recipe of incomingRecipes) {
         // Solo inicializamos si no existe, para no borrar lo que el usuario está editando
         if (!newZustandState[recipe.id] && recipe.products.commercial.length > 0) {
             newZustandState[recipe.id] = recipe.products.commercial;
             zustandUpdated = true;
         }
      }
      if(zustandUpdated) {
          useSearchStore.getState().initializeRecipeProducts(newZustandState);
      }
  
      setRecipes((prevRecipes) => {
        const currentRecipesMap = new Map(prevRecipes.map(r => [r.id, r]));
        const incomingIds = new Set(incomingRecipes.map(r => r.id));

        // 1. Procesar recetas que vienen del Padre (DB o respuesta de API)
        const mergedRecipes = incomingRecipes.map((incoming) => {
            const existing = currentRecipesMap.get(incoming.id);
            if (existing) {
                // Si ya existe localmente, preservamos el estado local (inputs, productos en memoria)
                // PERO actualizamos campos críticos que pudieron cambiar en el padre (ej: URL de imagen nueva)
                return {
                    ...existing,
                    imageUrl: incoming.imageUrl || existing.imageUrl, // Priorizamos la nueva URL si cambió
                    // Si hay otros campos que el padre actualiza (ej: fechas validadas), actualízalos aquí con cuidado
                    // de no sobrescribir input del usuario si el padre manda null.
                };
            }
            return incoming; // Es una receta nueva que viene de la DB/Padre
        });

        // 2. RECUPERAR RECETAS LOCALES NUEVAS (Que no están en el padre/DB todavía)
        // Esto soluciona el problema de que desaparezcan al cambiar imagen de otra receta.
        prevRecipes.forEach(prev => {
            // Si la receta existe en local PERO NO viene del padre...
            if (!incomingIds.has(prev.id)) {
                // Verificamos si es una receta "local nueva" (ej: creada con uid() y no guardada)
                // O si es una receta que acabamos de borrar.
                
                // ¿Cómo distinguimos? 
                // Las recetas borradas se eliminan de 'prevRecipes' en 'handleRemoveRecipe'.
                // Por lo tanto, si está en 'prevRecipes' en este ciclo de render, es que el usuario AÚN LA TIENE.
                // Si desapareció de 'initialRecipes' (props) es porque el padre se actualizó (ej: upload imagen)
                // y devolvió solo lo que tiene en DB.
                
                // Mantenemos la receta local en la lista
                mergedRecipes.push(prev);
            }
        });

        // Ordenamos o mantenemos orden si es necesario, o lo dejamos como está (Merged al final)
        return mergedRecipes;
      });
  
      // Ajuste del ActiveKey (sin cambios mayores, solo seguridad)
      setRecipes(current => {
          if (!activeKey || !current.find(r => r.id === activeKey)) {
             if (current.length > 0) {
                 setActiveKey(current[0].id);
                 useSearchStore.getState().setActiveRecipeId(current[0].id);
             }
          }
          return current;
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRecipes]);

  const productsByRecipe = useSearchStore((state) => state.productsByRecipe);

  const { deleteProduct, updateProductQuantity, setActiveRecipeId, clearProductsForRecipe } = useSearchStore(
    useShallow((state) => ({
      deleteProduct: state.deleteProduct,
      updateProductQuantity: state.updateProductQuantity,
      setActiveRecipeId: state.setActiveRecipeId,
      clearProductsForRecipe: state.clearProductsForRecipe,
    }))
  );

  useEffect(() => {
    let hasChanged = false;
    setRecipes((prevRecipes) => {
      const newRecipes = prevRecipes.map((recipe) => {
        const zustandProducts = productsByRecipe[recipe.id] || [];
        if (recipe.products.commercial !== zustandProducts) {
          hasChanged = true;
          return {
            ...recipe,
            products: {
              ...recipe.products,
              commercial: zustandProducts,
            },
          };
        }
        return recipe;
      });
      if (hasChanged) {
        onChange(newRecipes);
        onPersist(newRecipes);
      }
      return hasChanged ? newRecipes : prevRecipes;
    });
  }, [productsByRecipe, onChange, onPersist]);

  useEffect(() => {
    if (activeKey) {
      setActiveRecipeId(activeKey);
    }
    return () => {
      setActiveRecipeId(null);
    };
  }, [activeKey, setActiveRecipeId]);

  const openUpload = () => {
    setFileList([]);
    setUploadModalVisible(true);
  };



const handleUploadOk = async () => {
  if (fileList.length === 0) return;

  const file = fileList[0];
    setIsCreating(true); 

  try {
    let imageUrl = null;
    if (file.originFileObj) {
      imageUrl = URL.createObjectURL(file.originFileObj);
    }

    const savedRecipe = await handleAddRecipeService(file);

    if (savedRecipe && savedRecipe.id) {
      
      const newRecipe = {
        id: savedRecipe.id, 
        imageUrl: savedRecipe.url || imageUrl,
        patient: { first_name: '', last_name: '', rut: '' },
        professional: savedRecipe.professional || { first_name: '', last_name: '', rut: '', medical_center: '', email: '' },
        expiration_date: savedRecipe.expiration_date,
        renewal_date: savedRecipe.renewal_date,
        check_number: savedRecipe.check_number,
        products: { commercial: [], magistralFinished: [], magistralToMake: [] },
      };

      setRecipes(prev => {
        const next = [...prev, newRecipe];
        return next;
      });

      setActiveKey(savedRecipe.id);
      setActiveRecipeId(savedRecipe.id);

      setFileList([]);
      setUploadModalVisible(false);
    }

  } catch (error) {
    console.error("Error al subir receta:", error);
  } finally {
    setIsCreating(false);
  }
};

  const handleUploadCancel = () => setUploadModalVisible(false);
  const handleTabChange = (key) => {
    setActiveKey(key);
    setActiveRecipeId(key);
  };


  const handleRemoveRecipe = (id) => {
    Modal.confirm({
      title: 'Borrar receta',
      content: 'Al borrar la receta, también se borraran los productos asociados a la misma.',
      okText: 'Borrar',
      cancelText: 'Cancelar',
      okButtonProps: { danger: true },
      
      onOk: async () => {
        try {
          await handleRemoveRecipeService(id);
          clearProductsForRecipe(id);
            setRecipes(prev => {
            const next = prev.filter(r => r.id !== id);
            let newActiveKey = null;
            if (next.length > 0) {
               newActiveKey = next[0].id;
            }
            
            setActiveKey(newActiveKey);
            setActiveRecipeId(newActiveKey);
            return next;
          });
  
        } catch (error) {
          console.error("No se pudo eliminar la receta", error);
        }
      }
    });
  };


  const onUpdateImage = async (file) => {
    if (!activeKey) return false;

    setIsUpdatingImage(true);
    try {
        const newUrl = await handleChangeRecipeService(file, activeKey);
        
        if (newUrl) {
            setRecipes(prev => prev.map(r => 
                r.id === activeKey ? { ...r, imageUrl: newUrl } : r
            ));
        }
    } catch (error) {
        console.error("Error actualizando imagen", error);
    } finally {
        setIsUpdatingImage(false);
    }
    return false; 
};
  
  const handleFieldChange = (recipeId, path, value) => {
    setRecipes(prev => prev.map(r => {
      if (r.id !== recipeId) return r;
      if (path.startsWith('patient.')) {
        const key = path.split('.')[1];
        return { ...r, patient: { ...r.patient, [key]: value } };
      }
      if (path.startsWith('professional.')) {
        const key = path.split('.')[1];
        return { ...r, professional: { ...r.professional, [key]: value } };
      }
      if (['expiration_date', 'renewal_date', 'check_number'].includes(path)) {
        return {
          ...r,
          [path]: value,
        };
      }
    
    
      return r;
    }));
  };




  const handleMagistralProductSelect = (selectedProduct) => {
    const recipeId = activeKey;
    if (!recipeId) return;


    const newProduct = calculateMagistralData({
      id: selectedProduct.id,
      name: selectedProduct.name,
      specifications: selectedProduct.specifications,
      price_unit: selectedProduct.price,
      price_provider: selectedProduct.price_provider,
      laboratory: selectedProduct.laboratory,
      quantity: 1,
      discount_percent: 0,
      code_internal: selectedProduct.code_internal,
      image_url: selectedProduct.image_url,
      is_local: true
    });

    setRecipes(prevRecipes => prevRecipes.map(recipe => {
      if (recipe.id === recipeId) {
        const products = recipe.products.magistralFinished;
        if (products.some(p => p.id === newProduct.id)) return recipe;

        return {
          ...recipe,
          products: {
            ...recipe.products,
            magistralFinished: [...products, newProduct],
          },
        };
      }
      return recipe;
    }));

  };

  const handleUpdateMagistralQuantity = (productId, newQuantity) => {
    const recipeId = activeKey;
    if (!recipeId || !newQuantity) return;

    setRecipes(prevRecipes => prevRecipes.map(recipe => {
      if (recipe.id === recipeId) {
        const updatedProducts = recipe.products.magistralFinished.map(p => {
          if (p.id === productId) {
            const updatedProduct = { ...p, quantity: newQuantity };
            return calculateMagistralData(updatedProduct);
          }
          return p;
        });
        return { ...recipe, products: { ...recipe.products, magistralFinished: updatedProducts } };
      }
      return recipe;
    }));
  };

  const handleUpdateMagistralDiscount = (productId, newDiscountPercent) => {
    const recipeId = activeKey;
    if (!recipeId) return;

    setRecipes(prevRecipes => prevRecipes.map(recipe => {
      if (recipe.id === recipeId) {
        const updatedProducts = recipe.products.magistralFinished.map(p => {
          if (p.id === productId) {
            const updatedProduct = { ...p, discount_percent: newDiscountPercent };
            return calculateMagistralData(updatedProduct);
          }
          return p;
        });
        return { ...recipe, products: { ...recipe.products, magistralFinished: updatedProducts } };
      }
      return recipe;
    }));
  };

  const onDeleteCommercial = async (skuToDelete) => {
    const recipeId = activeKey;
    const currentProducts = useSearchStore.getState().productsByRecipe[recipeId] || [];
    
    // Buscar producto por varios criterios para asegurar match
    const product = currentProducts.find(p => 
        p.sku === skuToDelete || 
        p.code_internal === skuToDelete || 
        p.id_pharol === skuToDelete ||
        p.id === skuToDelete // Agregamos esto por si acaso
    );
    
    const dbId = product?.id; 

    if (dbId && isRealId(dbId)) {
       try {
         // 1. Borrar en Backend
         await handleRemoveCommercialProductService(dbId);
         
         // 2. Borrar de Zustand
         deleteProduct(skuToDelete); 

         // 3. FORZAR BORRADO EN ESTADO LOCAL (La clave que falta)
         setRecipes(prevRecipes => prevRecipes.map(r => {
            if (r.id === recipeId) {
                return {
                    ...r,
                    products: {
                        ...r.products,
                        // Filtramos explícitamente por el ID de base de datos
                        commercial: r.products.commercial.filter(p => p.id !== dbId)
                    }
                };
            }
            return r;
         }));

       } catch (error) {
         console.error("Error eliminando del backend", error);
         Modal.error({ title: "Error", content: "No se pudo eliminar el producto." });
       }
    } else {
       // Producto nuevo (solo local)
       deleteProduct(skuToDelete); 
       // El useEffect de sincronización de Zustand se encargará del resto aquí
    }
};


  

  const handleDeleteMagistralProduct = async (productId) => {
    const recipeId = activeKey;
    if (!recipeId) return;

    const currentRecipe = recipes.find(r => r.id === recipeId);
    const productToDelete = currentRecipe?.products?.magistralFinished?.find(p => p.id === productId);

 
    if (isRealId(productId) && !productToDelete?.is_local) { 
        try {
            await handleRemoveMagistralProduct(productId); 
             setRecipes(prevRecipes => prevRecipes.map(recipe => {
                if (recipe.id === recipeId) {
                    return {
                        ...recipe,
                        products: {
                            ...recipe.products,
                            magistralFinished: recipe.products.magistralFinished.filter(p => p.id !== productId),
                        },
                    };
                }
                return recipe;
            }));

        } catch (error) {
            console.error("Error eliminando producto magistral:", error);
        }
    } 
    else {
         setRecipes(prevRecipes => prevRecipes.map(recipe => {
            if (recipe.id === recipeId) {
                return {
                    ...recipe,
                    products: {
                        ...recipe.products,
                        magistralFinished: recipe.products.magistralFinished.filter(p => p.id !== productId),
                    },
                };
            }
            return recipe;
        }));
        
        toggleSectionVisibility(recipeId, 'magistralFinished', false); 
    }
};



  const handleAddFormulaToMake = (newFormula) => {
    const recipeId = activeKey;
    if (!recipeId) return;

    const finalProduct = calculateMagistralData({
      ...newFormula,
      id: uid(),
      price_unit: newFormula.price_unit_manual,
      discount_percent: 0,
    });

    setRecipes(prevRecipes => prevRecipes.map(recipe => {
      if (recipe.id === recipeId) {
        return {
          ...recipe,
          products: {
            ...recipe.products,
            magistralToMake: [...recipe.products.magistralToMake, finalProduct],
          },
        };
      }
      return recipe;
    }));
    toggleSectionVisibility(recipeId, 'magistralToMake', false);
  };

 


  const handleDeleteFormulaToMake = async (productId) => {
    const recipeId = activeKey;
    if (!recipeId) return;

    if (isRealId(productId)) {
        try {
           
            await handleRemoveMagistralProduct(productId);

            setRecipes(prevRecipes => prevRecipes.map(recipe => {
                if (recipe.id === recipeId) {
                    return {
                        ...recipe,
                        products: {
                            ...recipe.products,
                            magistralToMake: recipe.products.magistralToMake.filter(p => p.id !== productId),
                        },
                    };
                }
                return recipe;
            }));
        } catch (error) {
            console.error("Error eliminando producto magistral fórmula:", error);
        }
    } else {
        setRecipes(prevRecipes => prevRecipes.map(recipe => {
            if (recipe.id === recipeId) {
                return {
                    ...recipe,
                    products: {
                        ...recipe.products,
                        magistralToMake: recipe.products.magistralToMake.filter(p => p.id !== productId),
                    },
                };
            }
            return recipe;
        }));
    }
};


  const handleUpdateMagistralField = (listName, productId, field, value) => {
    const recipeId = activeKey;
    if (!recipeId) return;

    setRecipes(prevRecipes => prevRecipes.map(recipe => {
      if (recipe.id === recipeId) {
        const updatedProducts = recipe.products[listName].map(p => {
          if (p.id === productId) {
            const updatedProduct = { ...p, [field]: value };
            return calculateMagistralData(updatedProduct);
          }
          return p;
        });
        return { ...recipe, products: { ...recipe.products, [listName]: updatedProducts } };
      }
      return recipe;
    }));
  };


  const activeRecipe = recipes.find(r => r.id === activeKey);


  return (
    <div className="ph-recipes-module" style={{ marginTop: 24 }}>
      <CustomCard>
        <Row justify="space-between" align="middle">
          <Col></Col>
          <Col>
            <CustomButton disabled={statusNumber!==100} icon={<PlusOutlined />} onClick={openUpload}>Agregar receta</CustomButton>
          </Col>
        </Row>

        <div style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={16} style={{ position: 'relative', zIndex: 1 }}>
              <Tabs
                activeKey={activeKey}
                onChange={handleTabChange}
                type="editable-card"
                hideAdd
                tabBarGutter={8}
                onEdit={(targetKey, action) => {
                  if (action === 'remove') handleRemoveRecipe(targetKey);
                }}
              >
                {recipes.map((recipe, idx) => (
                  /**Se agrego el closable que le quita el X al tab */
                  <Tabs.TabPane tab={`Receta ${idx + 1}`} key={recipe.id} closable={false}> 
                    <div style={{ display: 'grid', gap: 16 }}>
                      <CustomCard>
                        <h4>Carga de productos</h4>
                        <p style={{ color: '#666' }}>
                          En caso de productos de recetario magistral, asegurate de tener los componentes para confeccionar el producto a la brevedad.
                          En caso de productos terminados o comerciales, procura tener stock antes de enviar la cotización.
                        </p>
                      </CustomCard>

                      <CustomCard>
                        <h4>Datos del titular</h4>
                        <Form layout="vertical">
                          <Row gutter={8}>
                            <Col span={8}>
                              <Form.Item label="Nombre titular" required>
                                <CustomInput
                                  disabled={statusNumber!==100}
                                  placeholder="Nombre"
                                  value={recipe.patient?.first_name}
                                  onChange={(e) => handleFieldChange(recipe.id, 'patient.first_name', e.target.value)}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item label="Apellido titular" required>
                                <CustomInput
                                  disabled={statusNumber!==100}
                                  placeholder="Apellido"
                                  value={recipe.patient?.last_name}
                                  onChange={(e) => handleFieldChange(recipe.id, 'patient.last_name', e.target.value)}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item label="RUT titular" required>
                                <CustomInput
                                  disabled={statusNumber!==100}
                                  placeholder="11111111-1"
                                  value={recipe.patient?.rut}
                                  onChange={(e) => handleFieldChange(recipe.id, 'patient.rut', e.target.value)}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Form>
                      </CustomCard>

                      <CustomCard>
                        <h4>Datos del profesional</h4>
                        <Form layout="vertical">
                          <Row gutter={8}>
                            <Col span={8}>
                              <Form.Item label="Nombre profesional">
                                <CustomInput
                                  disabled={statusNumber!==100}
                                  placeholder="Nombre"
                                  value={recipe.professional.first_name}
                                  onChange={(e) => handleFieldChange(recipe.id, 'professional.first_name', e.target.value)}
                                />
                              </Form.Item>

                            </Col>
                            <Col span={8}>
                              <Form.Item label="Apellido profesional">
                                <CustomInput
                                  disabled={statusNumber!==100}
                                  placeholder="Apellido"
                                  value={recipe.professional.last_name}
                                  onChange={(e) => handleFieldChange(recipe.id, 'professional.last_name', e.target.value)}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item label="RUT profesional">
                                <CustomInput
                                  disabled={statusNumber!==100}
                                  placeholder="11111111-1"
                                  value={recipe.professional.rut}
                                  onChange={(e) => handleFieldChange(recipe.id, 'professional.rut', e.target.value)}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row gutter={8} style={{ marginTop: 12 }}>
                            <Col span={12}>
                              <Form.Item label="Centro médico (opcional)">
                                <CustomInput
                                  disabled={statusNumber!==100}
                                  placeholder="Clinica ejemplo"
                                  value={recipe.professional.medical_center}
                                  onChange={(e) => handleFieldChange(recipe.id, 'professional.medical_center', e.target.value)}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item label="Corréo electrónico (opcional)">
                                <CustomInput
                                  disabled={statusNumber!==100}
                                  placeholder="ejemplo@mail.com"
                                  value={recipe.professional.email}
                                  onChange={(e) => handleFieldChange(recipe.id, 'professional.email', e.target.value)}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Form>
                      </CustomCard>

                      <CustomCard>
                        <h4>Datos de la receta</h4>
                        <Row gutter={8}>
                          <Col span={8}>
                            <Form.Item label="Fecha de expiración de la receta">
                              <DatePicker
                                disabled={statusNumber!==100}
                                style={{ width: '100%' }}
                                disabledDate={disabledDate}
                                placeholder="11/11/1111"
                                format="YYYY-MM-DD"
                                value={
                                  recipe.expiration_date 
                                    ? dayjs(recipe.expiration_date)
                                    : null
                                }
                                onChange={(date, dateString) => handleFieldChange(recipe.id, 'expiration_date', dateString)}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item label="Fecha de renovación del tratamiento">
                              <DatePicker
                                disabled={statusNumber!==100}
                                style={{ width: '100%' }}
                                disabledDate={disabledDate}
                                placeholder="11/11/1111"
                                format="YYYY-MM-DD"
                                value={
                                  recipe.renewal_date 
                                    ? dayjs(recipe.renewal_date)
                                    : null
                                }
                                onChange={(date, dateString) => handleFieldChange(recipe.id, 'renewal_date', dateString)}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item label="N° de Receta Cheque (opcional)">
                              <CustomInput
                                disabled={statusNumber!==100}
                                placeholder="111111"
                                value={recipe.check_number}
                                onChange={(e) => handleFieldChange(recipe.id, 'check_number', e.target.value)}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </CustomCard>


                      <CustomCard>
                      <Row justify="space-between" align="middle" style={{ marginBottom: recipe?.uiState?.commercial ? 16 : 0 }}>
                          <Col style={{ display: 'flex', alignItems: 'center' }}>
                            <h3>Producto comercial</h3>
                          </Col>
                          <Col>
                            {!recipe.uiState?.commercial && statusNumber === 100 && (
                              <CustomButton disabled={statusNumber!==100} onClick={() => toggleSectionVisibility(recipe.id, 'commercial', true)}>
                                Agregar
                              </CustomButton>
                            )}

                          </Col>
                        </Row>
                        <Row justify="space-between" align="middle">
                          <Col span={24}>
                            <ProductSection products={recipe?.products?.commercial}
                            // onDeleteProduct={deleteProduct}
                            onDeleteProduct={onDeleteCommercial}
                              onUpdateQuantity={updateProductQuantity} isSearchVisible={recipe?.uiState?.commercial}
                              status={statusNumber}
                              />
                          </Col>
                        </Row>
                      </CustomCard>

                      <CustomCard>
                        <Row justify="space-between" align="middle" style={{ marginBottom: recipe?.uiState?.magistralFinished ? 16 : 0 }}>
                          <Col style={{ display: 'flex', alignItems: 'center' }}>
                            <h3>Producto de recetario magistral terminado</h3>
                          </Col>

                          <Col>
                              {!recipe?.uiState?.magistralFinished && statusNumber === 100 && (
                              <CustomButton disabled={statusNumber!==100} onClick={() => toggleSectionVisibility(recipe.id, 'magistralFinished', true)}>
                                Agregar
                              </CustomButton>
                            )}


                          </Col>
                        </Row>

                        <Row>
                          <Col span={24}>
                            <MagistralFinishedSection
                             status={statusNumber}
                              products={recipe.products.magistralFinished}
                              onProductSelect={handleMagistralProductSelect}
                              onDelete={handleDeleteMagistralProduct}
                              onUpdateQuantity={handleUpdateMagistralQuantity}
                              onUpdateDiscount={handleUpdateMagistralDiscount}
                              isSearchVisible={recipe?.uiState?.magistralFinished}
                              onAddButtonVisibilityChange={(val) => toggleSectionVisibility(recipe.id, 'magistralFinished', val)}
                              onHideSearch={() => toggleSectionVisibility(recipe.id, 'magistralFinished', false)}                            />
                          </Col>
                        </Row>
                      </CustomCard>



                      <CustomCard>
                        <Row justify="space-between" align="middle">
                          <Col style={{ display: 'flex', alignItems: 'center' }}>
                            <h3>Producto de recetario magistral a confeccionar</h3>
                          </Col>
                          <Col>
                            {!recipe?.uiState?.magistralToMake && statusNumber === 100 && recipe.products.magistralToMake.length === 0  &&(
                              <CustomButton disabled={statusNumber!==100} onClick={() => toggleSectionVisibility(recipe.id, 'magistralToMake', true)}>
                                Agregar
                              </CustomButton>
                            )}

                          </Col>
                        </Row>

                        <Row>
                          <Col span={24}>
                            <MagistralToMakeSection
                              status={statusNumber}
                              products={recipe.products.magistralToMake}
                              isSearchVisible={recipe?.uiState?.magistralToMake || recipe?.products?.magistralToMake.length > 0}
                              onProductAdd={handleAddFormulaToMake}
                              onDelete={handleDeleteFormulaToMake}
                              onUpdateQuantity={(productId, newValue) => handleUpdateMagistralField('magistralToMake', productId, 'quantity', newValue)}
                              onUpdatePrice={(productId, newValue) => handleUpdateMagistralField('magistralToMake', productId, 'price_unit', newValue)}
                              onUpdateDiscount={(productId, newValue) => handleUpdateMagistralField('magistralToMake', productId, 'discount_percent', newValue)}
                              onAddButtonVisibilityChange={(val) => toggleSectionVisibility(recipe.id, 'magistralToMake', val)}                            />
                          </Col>
                        </Row>
                      </CustomCard>

                    </div>
                  </Tabs.TabPane>
                ))}
                {recipes.length === 0 && (
                  <Tabs.TabPane tab={`Sin recetas`} key="empty" disabled>
                    <Empty description="No hay recetas cargadas. Usa 'Agregar receta' para crear una." />
                  </Tabs.TabPane>
                )}
              </Tabs>
            </Col>

            <Col span={8}>
              <CustomCard style={{ minHeight: 360 }}>
                {activeRecipe && activeRecipe.imageUrl ? (
                  <>
                    <div style={{ textAlign: 'center' }}>
                      <CustomMediaViewer src={activeRecipe.imageUrl} />
                    </div>
                    <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'center' }}>
                      <CustomUpload
                        showUploadList={false}
                        disabled={isUpdatingImage || statusNumber !== 100}
                        beforeUpload={onUpdateImage} >
                        <CustomButton disabled={isUpdatingImage || statusNumber!==100} icon={<UploadOutlined loading={isUpdatingImage} />}>Cambiar receta</CustomButton>
                      </CustomUpload>
                      <CustomButton disabled={statusNumber!==100} danger icon={<DeleteOutlined />} onClick={() => handleRemoveRecipe(activeRecipe.id)}>Borrar receta</CustomButton>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: 'center' }}>
                    <Empty description="No hay imagen" />
                  </div>
                )}
              </CustomCard>
            </Col>
          </Row>
        </div>
      </CustomCard>
      <CustomModal open={uploadModalVisible} title="Agregar receta" onOk={handleUploadOk} onCancel={handleUploadCancel} okButtonProps={{ disabled: isCreating }} closable={!isCreating} cancelButtonProps={{ disabled: isCreating }} confirmLoading={isCreating}>
        <CustomUpload
          beforeUpload={(file) => {
            setFileList([{ originFileObj: file }]);
            return false;
          }}
          fileList={fileList}
          onRemove={() => setFileList([])}
          disabled={isCreating}
        >
          <CustomButton disabled={isCreating} icon={<UploadOutlined />}>Seleccionar archivo</CustomButton>
        </CustomUpload>
      </CustomModal>
    </div>
  );
}